import React, { useEffect, useMemo, useState } from "react";

const DESKTOP_VISIBLE_SCREENSHOTS = 3;
const MOBILE_VISIBLE_SCREENSHOTS = 1;
const MOBILE_MEDIA_QUERY = "(max-width: 768px)";

const rawSessions = [
  {
    id: "alphabets",
    files: Array.from({ length: 33 }, (_, index) => `${index + 1}.png`),
  },
  {
    id: "handwriting",
    files: ["1.png", "2.png", "3.png"],
  },
  {
    id: "mongolian-beginner",
    files: Array.from({ length: 10 }, (_, index) => `${index + 1}.png`),
  },
  {
    id: "english_a1",
    files: Array.from({ length: 14 }, (_, index) => `${index + 1}.png`),
  },
  {
    id: "korean-1",
    files: [
      ...Array.from({ length: 13 }, (_, index) => `${index + 1}.png`),
    ],
  },
  {
    id: "app",
    files: [
      ...Array.from({ length: 21 }, (_, index) => `${index + 1}.png`),
      "23.png",
      "24.png",
    ],
  },
];

const fallbackSessionLabels = {
  alphabets: "Alphabets",
  handwriting: "Handwriting",
  "mongolian-beginner": "Mongolian beginner",
  english_a1: "English beginner",
  "korean-1": "Korean beginner",
  app: "App",
};

const sortFilesByNameAsc = (files) =>
  [...files].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  );

const getPublicImagePath = (sessionId, fileName) =>
  `${process.env.PUBLIC_URL || ""}/images/tomko-app/${sessionId}/${fileName}`;

const getVisibleScreenshotCount = () => {
  if (typeof window === "undefined") {
    return DESKTOP_VISIBLE_SCREENSHOTS;
  }

  return window.matchMedia(MOBILE_MEDIA_QUERY).matches
    ? MOBILE_VISIBLE_SCREENSHOTS
    : DESKTOP_VISIBLE_SCREENSHOTS;
};

const chunkScreenshots = (screenshots, chunkSize) => {
  const pages = [];

  for (let index = 0; index < screenshots.length; index += chunkSize) {
    pages.push(screenshots.slice(index, index + chunkSize));
  }

  return pages;
};

const Tomko = ({
  labels = {},
  title = "Learning experience",
  screenshotInstruction = "Click on below screens for next",
}) => {
  const sessions = useMemo(
    () =>
      rawSessions
        .map((session) => ({
          ...session,
          title: labels[session.id] || fallbackSessionLabels[session.id] || session.id,
          screenshots: sortFilesByNameAsc(session.files).map((fileName) => ({
            fileName,
            src: getPublicImagePath(session.id, fileName),
          })),
        }))
        .filter((session) => session.screenshots.length > 0),
    [labels]
  );

  const [activeIndexes, setActiveIndexes] = useState(() =>
    sessions.reduce((indexes, session) => {
      indexes[session.id] = 0;
      return indexes;
    }, {})
  );
  const [activeSessionId, setActiveSessionId] = useState(sessions[0]?.id || "");
  const [visibleScreenshots, setVisibleScreenshots] = useState(
    getVisibleScreenshotCount
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = () => setVisibleScreenshots(getVisibleScreenshotCount());

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const activeSession =
    sessions.find((session) => session.id === activeSessionId) || sessions[0];

  const moveSession = (session, direction) => {
    const maxIndex = Math.max(
      Math.ceil(session.screenshots.length / visibleScreenshots) - 1,
      0
    );

    setActiveIndexes((current) => {
      const currentIndex = current[session.id] || 0;
      const nextIndex =
        direction === "next"
          ? currentIndex >= maxIndex
            ? 0
            : currentIndex + 1
          : currentIndex <= 0
          ? maxIndex
          : currentIndex - 1;

      return {
        ...current,
        [session.id]: nextIndex,
      };
    });
  };

  if (!activeSession) {
    return null;
  }

  const screenshotPages = chunkScreenshots(
    activeSession.screenshots,
    visibleScreenshots
  );
  const activeIndex = Math.min(
    activeIndexes[activeSession.id] || 0,
    Math.max(screenshotPages.length - 1, 0)
  );
  const canMove = screenshotPages.length > 1;
  const visibleScreenshotEnd = Math.min(
    (activeIndex + 1) * visibleScreenshots,
    activeSession.screenshots.length
  );
  const progressPercent =
    activeSession.screenshots.length > 0
      ? (visibleScreenshotEnd / activeSession.screenshots.length) * 100
      : 0;

  return (
    <section className="tomko-experience-section" aria-labelledby="tomko-experience-title">
      <div className="tomko-experience__inner">
        <div className="tomko-experience__header">
          <span className="tomko-experience__eyebrow">Tomko mobile app</span>
          <h2 id="tomko-experience-title">{title}</h2>
        </div>

        <article className="tomko-session">
          <div className="tomko-session__header">
            <div className="tomko-session__tabs" role="tablist" aria-label="Tomko app sessions">
              {sessions.map((session) => (
                <button
                  type="button"
                  role="tab"
                  className={`tomko-session__tab${
                    session.id === activeSession.id ? " active" : ""
                  }`}
                  aria-selected={session.id === activeSession.id}
                  key={session.id}
                  onClick={() => setActiveSessionId(session.id)}
                >
                  {session.title}
                </button>
              ))}
            </div>
            <div className="tomko-session__counter">
              <span>
                {activeIndex + 1} of {activeSession.screenshots.length} screen
                {activeSession.screenshots.length === 1 ? "" : "s"}
              </span>
              <p>{screenshotInstruction}</p>
            </div>
          </div>

          <div className="tomko-session__viewport">
            <div className="tomko-session__progress-row">
              <div
                className="tomko-session__progress"
                role="progressbar"
                aria-label={`${activeSession.title} screenshot progress`}
                aria-valuemin="0"
                aria-valuemax={activeSession.screenshots.length}
                aria-valuenow={visibleScreenshotEnd}
              >
                <span
                  className="tomko-session__progress-fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            <div
              className="tomko-session__track"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
              }}
            >
              {screenshotPages.map((page, pageIndex) => (
                <div className="tomko-session__page" key={pageIndex}>
                  {page.map((screenshot, index) => (
                    <div className="tomko-session__slide" key={screenshot.fileName}>
                      <button
                        type="button"
                        className="tomko-session__screenshot-button"
                        onClick={() => moveSession(activeSession, "next")}
                        disabled={!canMove}
                        aria-label={`Next ${activeSession.title} screenshots`}
                      >
                        <img
                          src={screenshot.src}
                          alt={`${activeSession.title} screenshot ${
                            pageIndex * visibleScreenshots + index + 1
                          }`}
                          loading="lazy"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="tomko-session__controls">
            <button
              type="button"
              onClick={() => moveSession(activeSession, "prev")}
              disabled={!canMove}
              aria-label={`Previous ${activeSession.title} screenshots`}
            >
              <span className="tomko-session__arrow" aria-hidden="true">
                &larr;
              </span>
              <span>Prev</span>
            </button>
            <button
              type="button"
              onClick={() => moveSession(activeSession, "next")}
              disabled={!canMove}
              aria-label={`Next ${activeSession.title} screens`}
            >
              <span>Next</span>
              <span className="tomko-session__arrow" aria-hidden="true">
                &rarr;
              </span>
            </button>
          </div>
        </article>
      </div>
    </section>
  );
};

export default Tomko;
